import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext.js";
import styles from "./InstallPrompt.module.css";

const DISMISSED_KEY = "lakseborsen:install_prompt_dismissed";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/** iPadOS reports itself as a Mac, so touch support is what gives it away. */
function isIosBrowserNotInstalled() {
  const isIos =
    /iPhone|iPad|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isInstalled =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return isIos && !isInstalled;
}

/**
 * Shows a small "add to home screen" banner using the browser's own
 * install prompt (Chrome/Edge/Android). iOS has no equivalent event, so
 * signed-in users there get short instructions instead — a signed-in
 * fisher is who the offline form helps, and a first-time visitor reading
 * about a river shouldn't have a banner over the page.
 */
export default function InstallPrompt() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [dismissed, setDismissed] = useState(
    () => !!localStorage.getItem(DISMISSED_KEY),
  );
  const [installEvent, setInstallEvent] =
    useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    function onBeforeInstallPrompt(e: Event) {
      e.preventDefault();
      setInstallEvent(e as BeforeInstallPromptEvent);
    }
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    return () =>
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
  }, []);

  function dismiss() {
    localStorage.setItem(DISMISSED_KEY, "1");
    setDismissed(true);
    setInstallEvent(null);
  }

  async function install() {
    if (!installEvent) return;
    await installEvent.prompt();
    dismiss();
  }

  if (dismissed) return null;

  const showIosHint =
    !installEvent && isAuthenticated && isIosBrowserNotInstalled();
  if (!installEvent && !showIosHint) return null;

  return (
    <div className={styles.banner} role="status">
      <span>
        {t(installEvent ? "installPrompt.text" : "installPrompt.iosHint")}
      </span>
      <div className={styles.actions}>
        {installEvent && (
          <button className={styles.installBtn} onClick={install}>
            {t("installPrompt.install")}
          </button>
        )}
        <button
          className={styles.dismissBtn}
          onClick={dismiss}
          aria-label={t("installPrompt.dismiss")}
        >
          ✕
        </button>
      </div>
    </div>
  );
}
